if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const btn = mediaToSwitch.querySelector('button');
          if (btn) {
            btn.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          }
        });

        this.initVerticalThumbnails();

        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      initVerticalThumbnails() {
        const thumbList = this.elements.thumbnails.querySelector('ul');
        const btnUp = this.elements.thumbnails.querySelector('.biop-thumb-arrow--up');
        const btnDown = this.elements.thumbnails.querySelector('.biop-thumb-arrow--down');
        const gradient = this.elements.thumbnails.querySelector('.biop-thumb-gradient-bottom');

        if (!thumbList) return;

        // Smooth mouse wheel scrolling
        thumbList.addEventListener(
          'wheel',
          (e) => {
            if (this.mql.matches && e.deltaY !== 0) {
              e.preventDefault();
              thumbList.scrollTop += e.deltaY * 0.8;
            }
          },
          { passive: false }
        );

        const updateScrollIndicators = () => {
          if (!this.mql.matches) return;
          const isScrollable = thumbList.scrollHeight > thumbList.clientHeight + 8;

          if (!isScrollable) {
            if (btnUp) btnUp.classList.add('is-hidden');
            if (btnDown) btnDown.classList.add('is-hidden');
            if (gradient) gradient.classList.add('is-hidden');
            return;
          }

          // Top arrow
          if (btnUp) {
            if (thumbList.scrollTop > 15) {
              btnUp.classList.remove('is-hidden');
            } else {
              btnUp.classList.add('is-hidden');
            }
          }

          // Bottom arrow & gradient
          const isAtBottom = thumbList.scrollTop + thumbList.clientHeight >= thumbList.scrollHeight - 15;
          if (btnDown) {
            if (isAtBottom) {
              btnDown.classList.add('is-hidden');
            } else {
              btnDown.classList.remove('is-hidden');
            }
          }

          if (gradient) {
            if (isAtBottom) {
              gradient.classList.add('is-hidden');
            } else {
              gradient.classList.remove('is-hidden');
            }
          }
        };

        thumbList.addEventListener('scroll', updateScrollIndicators);

        if (btnUp) {
          btnUp.addEventListener('click', () => {
            thumbList.scrollBy({ top: -90, behavior: 'smooth' });
          });
        }

        if (btnDown) {
          btnDown.addEventListener('click', () => {
            thumbList.scrollBy({ top: 90, behavior: 'smooth' });
          });
        }

        // Initial check and on window resize
        setTimeout(updateScrollIndicators, 300);
        window.addEventListener('resize', updateScrollIndicators);
      }

      onSlideChanged(event) {
        if (!event.detail || !event.detail.currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            if (activeMedia.parentElement && typeof activeMedia.parentElement.scrollTo === 'function') {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
            }
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll page if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (activeThumbnail) {
          this.setActiveThumbnail(activeThumbnail);
          if (activeThumbnail.dataset.mediaPosition) {
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        
        const btn = thumbnail.querySelector('button');
        if (btn) {
          btn.setAttribute('aria-current', 'true');
        }

        const thumbList = this.elements.thumbnails.querySelector('ul') || this.elements.thumbnails;

        // Auto-scroll the thumbnail into view smoothly
        if (thumbnail.scrollIntoView) {
          thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        } else {
          const thumbList = this.elements.thumbnails.querySelector('ul') || this.elements.thumbnails;
          if (thumbList && typeof thumbList.scrollTo === 'function') {
            if (this.mql.matches) {
              thumbList.scrollTo({ top: thumbnail.offsetTop, behavior: 'smooth' });
            } else {
              thumbList.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
            }
          }
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          if (!this.elements.liveRegion) return;
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = (window.accessibilityStrings?.imageAvailable || 'Image [index] is now available').replace('[index]', position);
          setTimeout(() => {
            if (this.elements.liveRegion) {
              this.elements.liveRegion.setAttribute('aria-hidden', true);
            }
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (typeof window.pauseAllMedia === 'function') {
          window.pauseAllMedia();
        }
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer || !this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        if (this.elements.viewer.sliderItems) {
          this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
        }
      }
    }
  );
}
